import type { IStorageService } from '../../contracts'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Web implementation of storage service using localStorage
 */
export class WebStorageService implements IStorageService {
  private storage: Storage

  constructor(useSessionStorage: boolean = false) {
    // Allow choosing between localStorage and sessionStorage
    this.storage = useSessionStorage ? sessionStorage : localStorage
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      this.storage.setItem(key, value)
      return Promise.resolve()
    } catch (error) {
      // Handle quota exceeded or other storage errors
      const logger = safeGetLogger()
      logger?.error('Storage set failed', { operation: 'set', error: String(error) })
      throw new Error(`Failed to store item with key: ${key}`)
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      const result = this.storage.getItem(key)
      return Promise.resolve(result)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Storage get failed', { operation: 'get', error: String(error) })
      return null
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      this.storage.removeItem(key)
      return Promise.resolve()
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Storage remove failed', { operation: 'remove', error: String(error) })
      throw new Error(`Failed to remove item with key: ${key}`)
    }
  }

  async clear(): Promise<void> {
    try {
      this.storage.clear()
      return Promise.resolve()
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Storage clear failed', { operation: 'clear', error: String(error) })
      throw new Error('Failed to clear storage')
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      const keys: string[] = []
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)
        if (key) {
          keys.push(key)
        }
      }
      return Promise.resolve(keys)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Storage get all keys failed', { operation: 'get', error: String(error) })
      return []
    }
  }

  async setObject<T>(key: string, value: T): Promise<void> {
    try {
      const serializedValue = JSON.stringify(value)
      await this.setItem(key, serializedValue)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Storage set object failed', { operation: 'set', error: String(error) })
      throw new Error(`Failed to store object with key: ${key}`)
    }
  }

  async getObject<T>(key: string): Promise<T | null> {
    try {
      const serializedValue = await this.getItem(key)
      if (serializedValue === null) {
        return null
      }
      return JSON.parse(serializedValue) as T
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Storage get object failed', { operation: 'get', error: String(error) })
      return null
    }
  }

  /**
   * Web-specific: Check if storage is available
   */
  static isStorageAvailable(type: 'localStorage' | 'sessionStorage' = 'localStorage'): boolean {
    try {
      const storage = window[type]
      const testKey = '__storage_test__'
      storage.setItem(testKey, 'test')
      storage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }

  /**
   * Web-specific: Get storage usage information
   */
  async getStorageInfo(): Promise<{ used: number; quota: number } | null> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate()
        return {
          used: estimate.usage || 0,
          quota: estimate.quota || 0,
        }
      } catch (error) {
        const logger = safeGetLogger()
        logger?.error('Storage get info failed', { operation: 'get', error: String(error) })
      }
    }
    return null
  }
}
